/**
 * Endpoint building utilities for legacy format conversion
 */
import {
  DEFAULT_ANCHORS,
  DEFAULT_LABELS,
  FIXED_HANDLE_ANCHORS,
  FIXED_HANDLE_LABELS,
} from '../shared/constants/index'
import type {
  LegacyAnchor,
  LegacySourceEndpoint,
  LegacyTargetEndpoint,
} from '../shared/legacyEndpoints'
import type { LegacyNodeKind } from '../shared/legacyNodes'
import { generateEndpointUuid } from '../shared/uuidUtils'

/**
 * Node kinds that have fixed handle configurations
 */
const FIXED_HANDLE_NODE_KINDS = [
  'schedule',
  'day_night',
  'queue',
  'transfer_rollover',
  'auto_attendant',
  'interactive_webhook',
  'ai_agent_v1',
] as const

type FixedHandleNodeKind = (typeof FIXED_HANDLE_NODE_KINDS)[number]

/**
 * Get anchor position and label for a handle based on sourceHandle and node kind.
 * Handles fixed handles for specific node types (schedule, day_night, queue, etc.)
 * and falls back to defaults for dynamic handles (option-*) or unknown handles.
 *
 * @param sourceHandle - The source handle identifier (e.g., 'whenOpen', 'onTimeout', 'option-0')
 * @param nodeKind - The legacy node kind to determine fixed handle configuration
 * @returns Object with anchor position and label for the handle. Returns defaults if sourceHandle is undefined or not recognized.
 */
export function getHandleAnchorAndLabel(
  sourceHandle: string | undefined,
  nodeKind: LegacyNodeKind,
): { anchor: LegacyAnchor; label: string } {
  const defaultAnchor: LegacyAnchor = DEFAULT_ANCHORS.source
  const defaultLabel = DEFAULT_LABELS.source

  if (!sourceHandle) {
    return { anchor: defaultAnchor, label: defaultLabel }
  }

  // Check if this node kind has fixed handles
  if (FIXED_HANDLE_NODE_KINDS.includes(nodeKind as FixedHandleNodeKind)) {
    const anchors = FIXED_HANDLE_ANCHORS[nodeKind as FixedHandleNodeKind]
    const labels = FIXED_HANDLE_LABELS[nodeKind as FixedHandleNodeKind]

    // Check if the sourceHandle exists in the anchors config
    if (sourceHandle in anchors) {
      const handleKey = sourceHandle as keyof typeof anchors
      return {
        anchor: anchors[handleKey],
        label: labels[handleKey],
      }
    }
  }

  // P2-3 Fix: Special handling for dial_name invalid handle
  // Legacy code (lines 6782-6785): invalid handle uses Right anchor with "Invalid" label (NOT "Drag")
  if (nodeKind === 'dial_name' && sourceHandle === 'invalid') {
    return {
      anchor: 'Right',
      label: 'Invalid',
    }
  }

  if (sourceHandle.startsWith('option-')) {
    return { anchor: defaultAnchor, label: defaultLabel }
  }

  return { anchor: defaultAnchor, label: sourceHandle }
}

/**
 * Create a source endpoint (outgoing connection) with proper typing for legacy format.
 * Generates a UUID in legacy format and creates a source endpoint object.
 *
 * @param kind - The legacy node kind used for UUID generation
 * @param nodeId - The node ID used for UUID generation
 * @param randomNumber - A unique random number used for UUID generation
 * @param targetUuid - The UUID of the target endpoint this source connects to, or null if unconnected
 * @param anchor - The anchor position on the node (e.g., 'Right', 'TopLeft', 'TopRight')
 * @param label - The label displayed for this endpoint (e.g., 'On Timeout', 'On Invalid')
 * @param optionId - Optional option ID for dynamic handles (call_distribution, auto_attendant, interactive_webhook)
 * @returns A properly typed LegacySourceEndpoint object
 */
export function createSourceEndpoint(
  kind: LegacyNodeKind,
  nodeId: string,
  randomNumber: number,
  targetUuid: string | null,
  anchor: LegacyAnchor,
  label: string,
  optionId?: string,
): LegacySourceEndpoint {
  const uuid = generateEndpointUuid(kind, nodeId, randomNumber)

  const endpoint: LegacySourceEndpoint = {
    anchor,
    is_source: true,
    uuid,
    target_uuid: targetUuid,
    my_label: label,
  }

  if (optionId) {
    endpoint.option_id = optionId
  }

  return endpoint
}

/**
 * Create a target endpoint (incoming connection) with proper typing for legacy format.
 * Generates a UUID in legacy format and creates a target endpoint object.
 *
 * @param kind - The legacy node kind used for UUID generation
 * @param nodeId - The node ID used for UUID generation
 * @param randomNumber - A unique random number used for UUID generation
 * @param sourceUuid - The UUID of the source endpoint this target connects to, or null if unconnected
 * @param anchor - The anchor position on the node (defaults to DEFAULT_ANCHORS.target, typically 'Left')
 * @param label - The label displayed for this endpoint (defaults to DEFAULT_LABELS.target)
 * @returns A properly typed LegacyTargetEndpoint object
 */
export function createTargetEndpoint(
  kind: LegacyNodeKind,
  nodeId: string,
  randomNumber: number,
  sourceUuid: string | null,
  anchor: LegacyAnchor = DEFAULT_ANCHORS.target,
  label: string = DEFAULT_LABELS.target,
): LegacyTargetEndpoint {
  const uuid = generateEndpointUuid(kind, nodeId, randomNumber)

  return {
    anchor,
    is_source: false,
    uuid,
    source_uuid: sourceUuid,
    my_label: label,
  }
}

/**
 * Build endpoint UUID maps for all edges in the React Flow graph.
 * This is the first pass of endpoint conversion: generates UUIDs for all source and target endpoints
 * based on the edges in the graph. The returned maps can be used to look up endpoint UUIDs
 * when creating legacy format endpoints.
 *
 * @param edges - Array of React Flow edges, each with an id, source node id, and target node id
 * @param nodeIdToKind - Map from node ID to legacy node kind, used for UUID generation
 * @param getUniqueRandomNumber - Function that returns a unique random number for UUID generation
 * @returns Object containing two maps:
 *   - nodeToSourceEndpoints: Map from node ID to Map from edge ID to source endpoint UUID
 *   - nodeToTargetEndpoints: Map from node ID to Map from edge ID to target endpoint UUID
 */
export function buildEndpointMaps(
  edges: Array<{ id: string; source: string; target: string }>,
  nodeIdToKind: Map<string, LegacyNodeKind>,
  getUniqueRandomNumber: () => number,
): {
  nodeToSourceEndpoints: Map<string, Map<string, string>>
  nodeToTargetEndpoints: Map<string, Map<string, string>>
} {
  const nodeToSourceEndpoints = new Map<string, Map<string, string>>()
  const nodeToTargetEndpoints = new Map<string, Map<string, string>>()

  edges.forEach((edge) => {
    const sourceKind = nodeIdToKind.get(edge.source)
    const targetKind = nodeIdToKind.get(edge.target)

    if (!sourceKind) {
      throw new Error(
        `[Legacy Converter] Edge "${edge.id}" references missing/unmapped source node "${edge.source}" while building endpoint UUIDs.`,
      )
    }

    if (!targetKind) {
      throw new Error(
        `[Legacy Converter] Edge "${edge.id}" references missing/unmapped target node "${edge.target}" while building endpoint UUIDs.`,
      )
    }

    const sourceRandom = getUniqueRandomNumber()
    const targetRandom = getUniqueRandomNumber()

    const sourceEndpointUuid = generateEndpointUuid(
      sourceKind,
      edge.source,
      sourceRandom,
    )

    if (!nodeToSourceEndpoints.has(edge.source)) {
      nodeToSourceEndpoints.set(edge.source, new Map())
    }
    const sourceEndpoints = nodeToSourceEndpoints.get(edge.source)
    if (sourceEndpoints) {
      sourceEndpoints.set(edge.id, sourceEndpointUuid)
    }

    const targetEndpointUuid = generateEndpointUuid(
      targetKind,
      edge.target,
      targetRandom,
    )

    if (!nodeToTargetEndpoints.has(edge.target)) {
      nodeToTargetEndpoints.set(edge.target, new Map())
    }
    const targetEndpoints = nodeToTargetEndpoints.get(edge.target)
    if (targetEndpoints) {
      targetEndpoints.set(edge.id, targetEndpointUuid)
    }
  })

  return {
    nodeToSourceEndpoints,
    nodeToTargetEndpoints,
  }
}
